/* eslint-disable warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
import EmberObject, { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import config from 'linced-app/config/environment';
import moment from 'moment';

export default class StudentsService extends Service {
  @service
  fetch;

  @service
  store;

  // allRevision invalidates `all` for every consumer; update() bumps it. The
  // memoised request below is deliberately NOT tracked: writing tracked state
  // from inside the `all` getter would dirty the very tag the reader just
  // consumed, which is an invalidation loop - the reader is asked to recompute,
  // writes again, and the render never settles.
  @tracked allRevision = 0;

  allRequest = null;
  allRequestRevision = -1;

  get all() {
    return this.requestAll();
  }

  // requestAll memoises the learner query until update() bumps the revision.
  // Reading `allRevision` here still entangles with whoever is reading `all` -
  // autotracking follows the call - while keeping the memo write out of the
  // getter body.
  requestAll() {
    let revision = this.allRevision;
    if (this.allRequest === null || this.allRequestRevision !== revision) {
      let request = this.store.query('person', {
        type: PersonType.LEARNER,
      });
      this.allRequest = request;
      this.allRequestRevision = revision;
      request.then((learners) => {
        if (this.isDestroyed || this.allRequest !== request) {
          // A newer request started meanwhile - it owns `resolved`.
          return;
        }
        this.resolved = learners;
      });
    }
    return this.allRequest;
  }

  // resolved holds the learner RecordArray once the query lands, empty until
  // then. `all` has to keep returning the promise - callers await it - but
  // array access on a PromiseArray is deprecated, so the counts below read
  // this instead. Written only from the request continuation (a later
  // microtask than the render that read it), matching `pendingUploads` below,
  // so it cannot invalidate a render in progress.
  @tracked resolved = [];

  @computed('resolved.@each.enrolmentStatus')
  get currentCount() {
    return this.resolved.filter(
      (person) => person.enrolmentStatus === EnrolmentStatus.CURRENT,
    ).length;
  }

  @computed('resolved.@each.enrolmentStatus')
  get currentLearnerIds() {
    return this.resolved
      .filter((person) => person.enrolmentStatus === EnrolmentStatus.CURRENT)
      .map((person) => person.id);
  }

  // This does not split on the homeroom computed. It used to return a
  // PromiseObject when it could not resolve a group synchronously, truthy from
  // the moment it was created whatever it later resolved to, which put every
  // pre-enrolled learner in the assigned bucket and pinned the unassigned
  // count at 0. It now reads only the store (HE-236), but this count keeps to
  // the homeroom id resolved below.
  //
  // A bare homeroomId is not enough either. A transfer learner keeps the old
  // school's homeroom id until PRE_ENROLLED becomes CURRENT (see the fallback
  // in person.homeroom), so the id alone counts them as assigned at a school
  // that has not placed them. _hasCurrentHomeroom resolves the id against the
  // store instead, which applicationData has already populated with this
  // school's current-year groups.
  @computed('resolved.@each.{enrolmentStatus,homeroomId}')
  get preenrolCount() {
    return this.resolved
      .filter(
        (person) => person.enrolmentStatus === EnrolmentStatus.PRE_ENROLLED,
      )
      .filter((person) => this._hasCurrentHomeroom(person)).length;
  }

  @computed('resolved.@each.{enrolmentStatus,homeroomId}')
  get otherPreenrolCount() {
    return this.resolved
      .filter(
        (person) => person.enrolmentStatus === EnrolmentStatus.PRE_ENROLLED,
      )
      .filter((person) => !this._hasCurrentHomeroom(person)).length;
  }

  // _hasCurrentHomeroom reports whether a learner holds a homeroom at this
  // school this year. It mirrors the synchronous half of person.homeroom, and
  // deliberately omits that computed's network fallback: a count has to answer
  // now, and an unresolved promise is what broke these counts to begin with.
  // An id belonging to another school is simply absent from the store, so it
  // correctly reads as unassigned.
  _hasCurrentHomeroom(learner) {
    let homeroomId = learner.homeroomId;
    if (isEmpty(homeroomId)) {
      return false;
    }
    let group = this.store.peekRecord('group', homeroomId);
    return !isEmpty(group) && group.year === moment().year();
  }

  @computed('resolved.@each.enrolmentStatus')
  get pendingCount() {
    return this.resolved.filter(
      (person) => person.enrolmentStatus === EnrolmentStatus.PENDING,
    ).length;
  }

  @computed('resolved.@each.enrolmentStatus')
  get birthdayStudents() {
    return this.resolved
      .filter((person) => person.enrolmentStatus === EnrolmentStatus.CURRENT)
      .filter(function (person) {
        return person.dob
          ? person.dob.substr(-5) === moment().format('MM-DD')
          : false;
      });
  }

  // pendingUploads holds the resolved ENROL upload counts, or null until the
  // first request lands. It is only ever written from a request continuation -
  // a later microtask than the render that read it - so it cannot invalidate a
  // render in progress.
  @tracked pendingUploads = null;

  pendingUploadsRequest = null;

  get numPendingEnrolments() {
    return this.pendingUploads?.numPendingEnrolments;
  }

  get numPendingUnenrolments() {
    return this.pendingUploads?.numPendingUnenrolments;
  }

  update() {
    // Bumping the revision is what makes existing consumers of `all` refetch.
    this.allRevision++;
    return this.all;
  }

  // loadPendingUploads resolves the pending ENROL upload counts. Concurrent
  // callers share one request; pass force to refetch. Templates read the counts
  // off `numPendingEnrolments` / `numPendingUnenrolments`, which update as soon
  // as this resolves.
  async loadPendingUploads(force = false) {
    if (this.pendingUploadsRequest === null || force) {
      let url = `${config.APP.apiHost}/people/${config.APP.apiPath}/enrolments/:countPendingUploads`;
      this.pendingUploadsRequest = this.fetch.request(url);
    }

    let request = this.pendingUploadsRequest;
    let counts = await request;
    if (request === this.pendingUploadsRequest) {
      // Still the current request - a forced refetch started meanwhile wins.
      this.pendingUploads = counts;
    }
    return counts;
  }

  updatePendingUploads() {
    return this.loadPendingUploads(true);
  }

  searchByJurisdictionalId(entity, jurisdictionalId) {
    let searchResponse = EmberObject.create({
      searchResult: null,
      existingPerson: null,
    });
    // A jurisdiction of 0 (the Go zero value) is not set.
    if (isEmpty(jurisdictionalId) || !entity.jurisdiction) {
      return Promise.resolve(searchResponse);
    }
    return this.fetch
      .request(
        `${config.APP.apiHost}/people/${config.APP.apiPath}/learners/:search?query=${jurisdictionalId}&jurisdiction=${entity.jurisdiction}`,
      )
      .then((response) => {
        if (!!response && !!response.learners && response.learners.length) {
          let learners = response.learners.filter((learner) => {
            return learner.personId !== entity.id;
          });
          let learner = learners[0];
          if (!isEmpty(learner)) {
            searchResponse.set('searchResult', learner);
            if (learner.inThisSchool || learner.isFormer) {
              // Fetch and populate the existing person, so that we can render a full user-badge-card for them.
              this.store
                .findRecord('person', learner.personId)
                .then((person) => {
                  searchResponse.set('existingPerson', person);
                });
            }
          }
        }
        return searchResponse;
      });
  }
}
